import { memo } from 'react'
import type { ChatCard, ThreadLoad } from './cardResults'
import { InvoicesTable } from './InvoicesTable'
import { CardPageFooter } from './CardPageFooter'
import { PodPreviewCard } from './PodPreviewCard'
import { LoadsTable } from './LoadsTable'
import { InvoicePreview } from './Previews'
import { ReadinessAnswer } from './ReadinessAnswer'
import { SingleLoadAnswer } from './SingleLoadAnswer'
import { InvoicingTable } from '../invoicing/InvoicingTable'
import { EmailResultsTable } from '../invoicing/EmailResultsTable'
import { FleetView } from '../assign/FleetView'
import { WriteRows } from './WriteRows'
import { useCardPagesStore } from './cardPagesStore'
import { pagedView } from './cardPages'

interface CardBodyProps {
    card: ChatCard
    loads: ThreadLoad[]
    /** The user's message the card answers, when the caller knows it. */
    question?: string
    onSend: (message: string) => void
}

/** A card's answer without its frame: the view its card and rows call for. An answer block draws each of its cards this way. */
export const CardBody = ({ card, loads, question, onSend }: CardBodyProps) => {
    const paged = useCardPagesStore((s) => s.byCard[card.id])
    const view = pagedView(card.view, paged)
    return (
        <>
            {view.view === 'invoices' && <InvoicesTable rows={view.rows} cardId={card.id} page={view.page} loads={loads} question={question} onSend={onSend} />}
            {view.view === 'invoice-preview' && (
                <div className="flex flex-col gap-2">
                    {view.invoices.map((invoice, i) => (
                        <InvoicePreview key={invoice.invoice_id ?? i} invoice={invoice} />
                    ))}
                    <CardPageFooter cardId={card.id} page={view.page} subject="invoices" />
                </div>
            )}
            {view.view === 'loads' && (
                <div className="flex flex-col gap-2">
                    <LoadsTable rows={view.rows} timezone={view.timezone} onSend={onSend} />
                    <CardPageFooter cardId={card.id} page={view.page} subject="loads" />
                </div>
            )}
            {view.view === 'load-preview' && <SingleLoadAnswer cardId={card.id} load={view.load} question={question} onSend={onSend} />}
            {view.view === 'writes' && <WriteRows card={view.card} rows={view.rows} onSend={onSend} />}
            {view.view === 'document' && <PodPreviewCard document={view.document} onSend={onSend} />}
            {view.view === 'invoicing' && <InvoicingTable messageId={card.id} worklist={view.worklist} quickAnswer={view.quickAnswer} question={question} />}
            {view.view === 'email-results' && <EmailResultsTable results={view.results} />}
            {view.view === 'readiness' && <ReadinessAnswer cardId={card.id} readiness={view.readiness} question={question} onSend={onSend} />}
            {view.view === 'fleet' && <FleetView fleet={view.fleet} />}
        </>
    )
}

/** A card's answer in its own frame. Drawn again only when its card (kept by id across reads), the thread's loads or the send changes. */
export const ChatCardView = memo(function ChatCardView(props: CardBodyProps) {
    return (
        <section
            aria-label={props.card.card ?? 'Card result'}
            className="w-full max-w-[95%] self-start rounded-2xl bg-surface p-3 text-foreground shadow-surface"
        >
            <CardBody {...props} />
        </section>
    )
})
